import { ArtistImage } from "models/ArtistImage";
import { useState } from "react";
import { ApiService } from "services/ApiService";
import ProfilePictureCropper from "./ProfilePictureCropper";

interface ArtistImageProps {
  artistImage: ArtistImage;
  artistId: string;
  isVisitor: boolean;
  onClick?: () => void;
  onDeleteClick?: (image: ArtistImage) => void;
  onImageUpdate?: (image: ArtistImage) => void;
  isEditable?: boolean;
  artistName?: string;
}

const ArtistImageComponent: React.FC<ArtistImageProps> = ({ artistImage, artistId, isVisitor, onClick, onDeleteClick, onImageUpdate, isEditable, artistName }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/artists/${artistId}/ArtistImages/${artistImage.id}`;
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(artistImage.name);
  const [description, setDescription] = useState(artistImage.description || '');
  const [showCropper, setShowCropper] = useState(false);

  const displayName = artistName || artistImage.name || 'Artist';

  const handleSave = async () => {
    try {
      const response = await ApiService.updateArtistImage(artistId, artistImage.id, name, description);
      if (response.success) {
        setIsEditing(false);
        if (onImageUpdate) {
          onImageUpdate({ ...artistImage, name, description });
        }
      } else {
        console.error("Failed to update image:", response.errors);
        alert("Failed to update the image. Please try again.");
      }
    } catch (error) {
      console.error("Error updating image:", error);
      alert("An error occurred while updating the image.");
    }
  };

  const handleSetAsProfile = () => {
    setShowCropper(true);
  };

  const handleCropperComplete = () => {
    setShowCropper(false);
    alert("Profile picture updated!");
    // Note: The cropper uploads a new ArtistImage as a profile picture.
    // We deliberately do NOT call onImageUpdate here because the gallery
    // was not modified — a new image was created, not an existing one edited.
    // The gallery will pick up the new image on next fetch (e.g., navigation).
  };

  const handleCropperClose = () => {
    setShowCropper(false);
  };

  return (
    <div className="text-center p-4 bg-gray-100 rounded-lg shadow-md">
      {isEditing ? (
        <>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Image title"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Description (optional)"
            rows={2}
          />
          <img
            src={endpoint}
            alt={artistImage.name}
            className="max-w-full h-96 object-cover rounded-lg mb-4"
          />
          <button
            onClick={handleSave}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 mr-2"
          >
            Save
          </button>
          <button
            onClick={() => onDeleteClick && onDeleteClick(artistImage)}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            Delete
          </button>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{artistImage.name}</h2>
          <div onClick={onClick} className="cursor-pointer">
          <img
            src={endpoint}
            alt={artistImage.name}
            className="max-w-full h-96 object-cover rounded-lg mb-4"
          />
          </div>
          <p className="text-gray-600">{artistImage.description}</p>
          {!isVisitor && isEditable && (
            <div className="flex gap-2 justify-center mt-4 flex-wrap">
              <button
                onClick={() => setIsEditing(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Edit
              </button>
              <button
                onClick={handleSetAsProfile}
                className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                Set as Profile Picture
              </button>
            </div>
          )}
        </>
      )}

      {showCropper && (
        <ProfilePictureCropper
          imageUrl={endpoint}
          artistId={artistId}
          artistName={displayName}
          onClose={handleCropperClose}
          onComplete={handleCropperComplete}
        />
      )}
    </div>
  );
};

export default ArtistImageComponent;